import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Search, Plus, Filter, MoreHorizontal, TrendingUp, TrendingDown, X } from "lucide-react";
import { API_URL } from "./config";
import "./usersControl.css";

// Maps a user's `access` value to the status pill: Granted -> Active,
// Pending -> Pending, anything else -> Inactive.
function getUserStatus(access) {
  if (access === "Granted") return { className: "status active", label: "Active" };
  if (access === "Pending") return { className: "status pending", label: "Pending" };
  return { className: "status inactive", label: "Inactive" };
}

const StatusBadge = ({ access }) => {
  const { className, label } = getUserStatus(access);
  return <span className={className}>{label}</span>;
};

const UsersControl = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("Users");
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [filterStatus, setFilterStatus] = useState("All");
  const [newUser, setNewUser] = useState({ name: "", email: "", role: "" });
  const filterRef = useRef(null);
  const itemsPerPage = 5;

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user")) || {};
    if (storedUser.email) fetchUsers(storedUser.email);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setShowFilter(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchUsers = async (email) => {
    try {
      const res = await axios.get(`${API_URL}/users`, { params: { email } });
      if (Array.isArray(res.data)) setUsers(res.data);
      else if (Array.isArray(res.data.users)) setUsers(res.data.users);
      else setUsers([]);
    } catch {
      setUsers([]);
    }
  };

  const handleAddUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.role) return;
    try {
      const storedUser = JSON.parse(localStorage.getItem("user")) || {};
      await axios.post(`${API_URL}/users`, { ...newUser, createdBy: storedUser.email });
      fetchUsers(storedUser.email);
    } catch {}
    setShowAddModal(false);
    setNewUser({ name: "", email: "", role: "" });
  };

  useEffect(() => {
    if (activeTab === "Permissions") navigate("/permissions");
    if (activeTab === "Roles") navigate("/roles");
  }, [activeTab]);

  const filteredUsers = users.filter((u) => {
    const matchSearch =
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase());
    const matchFilter =
      filterStatus === "All" ||
      (filterStatus === "Active" && u.access === "Granted") ||
      (filterStatus === "Inactive" && u.access !== "Granted" && u.access !== "Pending") ||
      (filterStatus === "Pending" && u.access === "Pending");
    return matchSearch && matchFilter;
  });

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, startIndex + itemsPerPage);

  const stats = [
    { label: "Total Users", value: users.length, change: 12, trend: "up" },
    { label: "Active Users", value: users.filter((u) => u.access === "Granted").length, change: 10, trend: "up" },
    { label: "Inactive Users", value: users.filter((u) => u.access !== "Granted" && u.access !== "Pending").length, change: 3, trend: "down" },
    { label: "Pending Invitations", value: users.filter((u) => u.access === "Pending").length, change: 8, trend: "up" },
  ];

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  return (
    <div className="users-page">
      {/* HEADER */}
      <div className="users-header">
        <div className="tabs">
          {["Users", "Roles", "Permissions"].map((tab) => (
            <button
              key={tab}
              className={`tab-btn ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>
        <button className="add-user-btn" onClick={() => navigate("/add-user")}>
          <Plus size={18} />
          Add User
        </button>
      </div>

      {activeTab === "Roles" && (
        <div className="tab-content"><p>Roles content coming soon.</p></div>
      )}
      {activeTab === "Permissions" && null}

      {activeTab === "Users" && (
        <>
          {/* STATS */}
          <div className="stats-grid">
            {stats.map((stat) => (
              <div className="stat-card" key={stat.label}>
                <p className="stat-label">{stat.label}</p>
                <h3 className="stat-value">{stat.value}</h3>
                <div className="stat-change">
                  <span className={`change-badge ${stat.trend}`}>
                    {stat.trend === "up" ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                    {stat.change}%
                  </span>
                  <span className="change-text">vs last 30 days</span>
                </div>
              </div>
            ))}
          </div>

          {/* TABLE CARD */}
          <div className="table-card">
            <div className="table-top">
              <div className="table-top-row">
                <h4>User List</h4>
                <div className="filter-wrapper" ref={filterRef}>
                  <button className="filter-btn" onClick={() => setShowFilter((p) => !p)}>
                    <Filter size={16} />
                    {filterStatus === "All" ? "Filters" : filterStatus}
                  </button>
                  {showFilter && (
                    <div className="filter-dropdown">
                      {["All", "Active", "Inactive", "Pending"].map((s) => (
                        <button
                          key={s}
                          className={`filter-option ${filterStatus === s ? "selected" : ""}`}
                          onClick={() => { setFilterStatus(s); setCurrentPage(1); setShowFilter(false); }}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="search-box">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                />
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last Active</th>
                  <th>MFA</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="user-cell">
                        <div className="avatar">{user.name?.charAt(0).toUpperCase()}</div>
                        <span className="user-name">{user.name}</span>
                      </div>
                    </td>
                    <td className="email-cell">{user.email}</td>
                    <td className="role-cell">{user.role}</td>
                    <td>
                      <StatusBadge access={user.access} />
                    </td>
                    <td className="last-active">{user.lastActive || "N/A"}</td>
                    <td>
                      <span className={user.mfa === "Enabled" || user.mfa === true ? "mfa enabled" : "mfa disabled"}>
                        {user.mfa === true || user.mfa === "Enabled" ? "Enabled" : "Disabled"}
                      </span>
                    </td>
                    <td>
                      <button className="action-btn"><MoreHorizontal size={18} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="table-footer">
              <span className="showing-text">
                Showing {filteredUsers.length === 0 ? 0 : startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredUsers.length)} of {filteredUsers.length} users
              </span>
              <div className="pagination">
                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="pagination-btn">&lt;</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button key={page} onClick={() => handlePageChange(page)} className={`pagination-btn ${currentPage === page ? "active" : ""}`}>
                    {page}
                  </button>
                ))}
                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0} className="pagination-btn">&gt;</button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ADD USER MODAL */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New User</h3>
              <button className="modal-close" onClick={() => setShowAddModal(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <label>Name</label>
              <input
                type="text"
                placeholder="Full name"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              />
              <label>Email</label>
              <input
                type="email"
                placeholder="Email address"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              />
              <label>Role</label>
              <select value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}>
                <option value="">Select role</option>
                <option value="Admin">Admin</option>
                <option value="Editor">Editor</option>
                <option value="Viewer">Viewer</option>
              </select>
            </div>
            <div className="modal-footer">
              <button className="modal-cancel" onClick={() => setShowAddModal(false)}>Cancel</button>
              <button className="modal-submit" onClick={handleAddUser}>Add User</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default UsersControl;